import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { queryClient } from '@/lib/utils/queryClient';
import { fallback } from '@/lib/utils/zodFallback';
import { EditStandardSheet } from '../-components/EditStandardSheet';
import { StandardsTable } from '../-components/StandardsTable';
import { adminStandardsQueryOptions } from '../-hooks/useAdminStandards';
import { useStandardsPage } from '../-hooks/useStandardsPage';

const standardsSearch = z.object({
  page: fallback(z.number().int().min(1), 1),
  limit: fallback(z.number().int().min(10).max(500), 50),
  search: z.string().optional(),
  sort: z
    .string()
    .transform(v => (/^\w+:(ASC|DESC)$/.test(v) ? v : undefined))
    .optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.string(), z.array(z.string())),
    ])
    .optional(),
});

export const Route = createFileRoute('/management/standards/(pages)/')({
  validateSearch: standardsSearch,
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    limit: search.limit ?? 50,
    ...(search.sort !== undefined ? { sort: search.sort } : {}),
    ...(search.search !== undefined ? { search: search.search } : {}),
    ...(search.filters !== undefined ? { filters: search.filters } : {}),
  }),
  // The rejection is swallowed on purpose. The loader is a prefetch — letting
  // it reject hands the route to the router's error boundary, which replaces
  // the page and hides the table's own "Could not load…" banner. That banner is
  // what stops a failed load from reading as an empty catalogue. Matches the
  // other management lists; the component's `useQuery` surfaces it inline.
  loader: ({ deps }) =>
    queryClient
      .ensureQueryData(adminStandardsQueryOptions(deps))
      .catch(() => undefined),
  component: StandardsPage,
});

function StandardsPage() {
  const page = useStandardsPage();

  return (
    <PageContainer>
      <StandardsTable page={page} />
      <EditStandardSheet page={page} />
    </PageContainer>
  );
}
